<template>
  <div class="state-chemistry-districts">
    <h4 v-if="title" class="state-chemistry-districts__heading has-text-dark" v-html="title"></h4>

    <template v-if="!stateCode">
      <p class="state-chemistry-districts__no-data">
        No state selected. Please configure the State attribute on this block.
      </p>
    </template>

    <template v-else-if="hasData">
      <!-- District selector — same markup as the search-bar InputSelect, wired to emit the district value -->
      <div class="state-chemistry-districts__filter box is-shadowless has-background-primary-lightest overflow-visible">
        <div class="search-form">
          <label :for="selectId" class="label has-text-dark">{{ selectLabel }}</label>
          <div class="is-flex is-align-items-end state-chemistry-districts__filter-row">
            <div class="inputSearch" style="flex: 1 1 auto; min-width: 0; width: 100%;">
              <v-select
                :input-id="selectId"
                v-model="selected"
                :reduce="({ value }) => value"
                :filterable="false"
                :clearSearchOnBlur="() => false"
                :clearSearchOnSelect="false"
                :options="dataFiltered"
                label="label"
                @search="(res) => (search = res)"
                @search:focus="onSearchFocus"
                @search:blur="onSearchBlur"
              >
                <template #search="{ attributes, events }">
                  <input
                    class="vs__search"
                    v-bind="{ ...attributes, value: displayValue }"
                    v-on="events"
                  />
                </template>
                <template #option="{ label }">
                  <span v-html="label"></span>
                </template>
                <template #open-indicator="{ attributes }">
                  <span class="icon-chevron-down" v-bind="attributes"></span>
                </template>
              </v-select>
            </div>
            <a
              v-if="downloadUrl"
              :href="downloadUrl"
              class="button is-primary is-dark"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i class="icon is-medium" v-icon:download-alt></i>
              <span>{{ downloadLabel }}</span>
            </a>
          </div>
        </div>
      </div>

      <!-- Metric cards -->
      <div class="columns is-multiline is-mobile">
        <div
          v-for="metric in metrics"
          :key="`${selected}-${metric.key}`"
          class="column is-6-mobile is-4-tablet is-3-desktop"
        >
          <div class="state-chemistry-districts__metric is-flex is-flex-direction-column is-align-items-center has-text-centered has-background-white p-5">
            <div class="state-chemistry-districts__metric-head is-flex is-flex-direction-column is-align-items-center is-justify-content-center mb-2">
              <span v-if="metric.icon" class="state-chemistry-districts__metric-icon">
                <i :class="metric.icon" aria-hidden="true"></i>
              </span>
              <span
                class="state-chemistry-districts__metric-value has-text-weight-bold"
                v-html="metric.display"
              ></span>
            </div>
            <h5 class="state-chemistry-districts__metric-label">{{ metric.label }}</h5>
            <span
              v-if="metric.note"
              class="state-chemistry-districts__metric-note"
            >{{ metric.note }}</span>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <p class="state-chemistry-districts__no-data">
        No district data available for {{ stateCode }}.
      </p>
    </template>
  </div>
</template>

<script>
export default {
  name: "StateChemistryDistricts",
  props: {
    title: { type: String, default: "" },
    stateCode: { type: String, default: "" },
    // Full URL for the state-level PDF (e.g. "https://…/state-data/California.pdf")
    stateDownloadUrl: { type: String, default: "" },
    // Base URL for district PDFs; the component appends "{StateCode}{NN}.pdf"
    // (e.g. "https://…/district-data/" → "https://…/district-data/CA01.pdf")
    districtDownloadBaseUrl: { type: String, default: "" },
    // Label shown for the statewide / default selection
    stateLabel: { type: String, default: "— State Overview —" },
    // Badge text shown when no district is selected
    overviewLabel: { type: String, default: "Statewide overview" },
    selectLabel: { type: String, default: "Select a Congressional District" },
    // [{ key, label, format, figurePrefix, figureSuffix, icon, note }]
    config: { type: Array, default: () => [] },
    // [{ id, num, values: { key: value } }]
    districts: { type: Array, default: () => [] },
    // { key: value } — statewide values shown by default
    stateData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      // null === statewide overview
      selected: null,
      // Live filter term while typing (empty = show the selected label).
      search: "",
      // Per-key value currently rendered, tweened from 0 to its target.
      animatedValues: {},
      // requestAnimationFrame handle for the running count-up.
      rafId: null,
    };
  },
  computed: {
    selectId() {
      return `scd-select-${this.stateCode}`;
    },
    hasData() {
      return this.districts.length > 0;
    },
    districtOptions() {
      return [
        { label: this.stateLabel, value: null },
        ...this.districts.map((d) => ({
          label: `District ${d.num}`,
          value: d.id,
        })),
      ];
    },
    // Mirrors InputSelect.vue: filter by the typed term and wrap matches so the
    // dropdown highlights them (`label` is the highlighted HTML, `labelOriginal`
    // the plain text echoed into the search input on select).
    dataFiltered() {
      const term = this.search ? String(this.search).toLowerCase() : "";
      // Escape regex metacharacters so typing "(", "[", "\", etc. can't throw.
      const safe = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      return this.districtOptions
        .filter((el) => el.label.toLowerCase().includes(term))
        .map((el) => {
          const regex = new RegExp(safe, "gi");
          return {
            value: el.value,
            labelOriginal: el.label,
            label: this.search
              ? el.label.replace(
                  regex,
                  (str) => `<span class="text-blue">${str}</span>`
                )
              : el.label,
          };
        });
    },
    // Plain label of the current selection, shown in the input when not editing.
    selectedLabel() {
      const opt = this.districtOptions.find((o) => o.value === this.selected);
      return opt ? opt.label : this.stateLabel;
    },
    // Show the live term while typing, otherwise the selected label — so the
    // chosen option stays visible even when the field is focused.
    displayValue() {
      return this.search || this.selectedLabel;
    },
    activeDistrict() {
      if (this.selected === null) return null;
      return this.districts.find((d) => d.id === this.selected) || null;
    },
    activeValues() {
      return this.activeDistrict ? this.activeDistrict.values : this.stateData;
    },
    downloadUrl() {
      if (this.selected !== null && this.activeDistrict && this.districtDownloadBaseUrl) {
        const num = String(this.activeDistrict.num).padStart(2, "0");
        return `${this.districtDownloadBaseUrl}${this.stateCode}${num}.pdf`;
      }
      return this.stateDownloadUrl || "";
    },
    downloadLabel() {
      if (this.selected !== null && this.activeDistrict) {
        return `Download District ${this.activeDistrict.num} Statistics`;
      }
      return "Download Statewide Statistics";
    },
    metrics() {
      return this.config
        .map((stat) => {
          const value = this.activeValues[stat.key];
          if (value === undefined || value === null) return null;
          const animated = this.animatedValues[stat.key];
          return {
            key: stat.key,
            label: stat.label,
            icon: stat.icon || "",
            note: stat.note || "",
            display: this.fmtValue(
              animated !== undefined ? animated : value,
              stat,
              value
            ),
          };
        })
        .filter((m) => m !== null);
    },
  },
  created() {
    // Seed every metric at 0 so the first paint starts the count-up from zero
    // instead of flashing the final value.
    const seed = {};
    this.config.forEach((stat) => {
      const v = this.activeValues[stat.key];
      seed[stat.key] = typeof v === "number" && isFinite(v) ? 0 : v;
    });
    this.animatedValues = seed;
  },
  mounted() {
    this.runCountUp();
  },
  beforeDestroy() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
  },
  watch: {
    // Replay the count-up whenever the active district changes.
    selected() {
      this.runCountUp();
    },
  },
  methods: {
    // On focus, list every option (clear the term) but keep the selected label
    // visible; select its text so the first keystroke replaces it when filtering.
    onSearchFocus() {
      this.search = "";
      this.$nextTick(() => {
        const input = this.$el.querySelector("input.vs__search");
        if (input) input.select();
      });
    },
    // On blur, drop any unconfirmed term so the selected label shows again.
    onSearchBlur() {
      this.search = "";
    },
    // Tween every numeric metric from 0 to its target value. Non-numeric
    // values (pre-rendered HTML, strings) are shown as-is without animation.
    runCountUp() {
      if (this.rafId) cancelAnimationFrame(this.rafId);

      const duration = 1200;
      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
      const targets = {};
      this.config.forEach((stat) => {
        const v = this.activeValues[stat.key];
        targets[stat.key] = typeof v === "number" && isFinite(v) ? v : null;
      });

      const start = performance.now();
      const step = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = easeOutCubic(progress);
        const next = {};
        this.config.forEach((stat) => {
          const target = targets[stat.key];
          if (target === null) {
            next[stat.key] = this.activeValues[stat.key];
          } else {
            next[stat.key] = progress < 1 ? target * eased : target;
          }
        });
        this.animatedValues = next;
        if (progress < 1) {
          this.rafId = requestAnimationFrame(step);
        } else {
          this.rafId = null;
        }
      };
      this.rafId = requestAnimationFrame(step);
    },
    numberFormat(num, decimals) {
      return Number(num).toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    },
    // Mirrors StateChemistryService::formatStat() on the CMS side.
    //
    // `target` is the final value of the metric. During the count-up `value` is the
    // animated figure, but the unit has to be picked from the target — otherwise a
    // billion-scale metric starts out reading "0M" and flips to "B" mid-animation.
    fmtValue(value, stat, target) {
      if (value === null || value === "") return "—";

      const raw = `${value}`;
      if (raw.charAt(0) === "<") return value; // pre-rendered HTML passthrough

      const prefix = stat.figurePrefix != null ? stat.figurePrefix : "";
      const suffix = stat.figureSuffix != null ? stat.figureSuffix : "";
      const scale = target !== undefined && target !== null ? target : value;

      switch (stat.format) {
        case "millions": {
          // Values are expressed in millions. Reduce to B if >= 1,000M.
          // One decimal if fractional after rounding, none if whole.
          if (Math.round(Math.abs(scale)) >= 1000) {
            const bs = value / 1000;
            const bdec = Math.round(bs * 10) / 10 === Math.round(bs) ? 0 : 1;
            return `${prefix}${this.numberFormat(bs, bdec)}B`;
          }
          const mr = Math.round(value * 10) / 10;
          const mdec = mr === Math.round(mr) ? 0 : 1;
          return `${prefix}${this.numberFormat(value, mdec)}M`;
        }
        case "currency": {
          const cprefix = stat.figurePrefix != null ? stat.figurePrefix : "$";
          return this.reduceMagnitude(value, cprefix);
        }
        case "rank":
          return `#${this.numberFormat(value, 0)}`;
        default:
          // 'integer' and any other format: reduce to K/M/B.
          return this.reduceMagnitude(value, prefix, suffix);
      }
    },
    reduceMagnitude(value, prefix, fallbackSuffix) {
      const abs = Math.abs(value);
      const units = [
        [1e9, "B"],
        [1e6, "M"],
        [1e3, "K"],
      ];
      for (const [threshold, unit] of units) {
        if (abs >= threshold) {
          const s = value / threshold;
          const dec = s === Math.round(s) ? 0 : 1;
          return `${prefix}${this.numberFormat(s, dec)}${unit}`;
        }
      }
      const dec = value === Math.round(value) ? 0 : 1;
      return `${prefix}${this.numberFormat(value, dec)}${fallbackSuffix || ""}`;
    },
  },
};
</script>

<style lang="scss" scoped></style>
